import { type VFC, useEffect, useRef, useState } from 'react'

import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import styles from './BackgroundPicker.module.scss'
import SidebarItem from '../Sidebar/SidebarItem'

export const STORY_BACKGROUNDS = {
    modernGradient:
        'radial-gradient(circle at 55% 10%, rgba(78,255,236,.35), transparent 35%), radial-gradient(circle at 85% 80%, rgba(96,145,255,.35), transparent 35%), radial-gradient(circle at 0% 100%, rgba(255,255,255,.18), transparent 45%), linear-gradient(135deg, #b45cff 0%, #8c7cff 35%, #89b3ff 70%, #d8e4ea 100%)',

    pastelAbstract:
        'radial-gradient(ellipse at 8% 12%, rgba(255,176,203,.75) 0%, rgba(255,176,203,.35) 18%, transparent 42%), radial-gradient(ellipse at 95% 5%, rgba(255,255,255,.85) 0%, rgba(255,255,255,.45) 24%, transparent 52%), radial-gradient(ellipse at 5% 95%, rgba(55,145,255,.75) 0%, rgba(55,145,255,.35) 28%, transparent 58%), radial-gradient(ellipse at 58% 80%, rgba(202,164,255,.45) 0%, transparent 42%), radial-gradient(ellipse at 82% 62%, rgba(104,220,230,.38) 0%, transparent 45%), radial-gradient(ellipse at 18% 58%, rgba(255,255,255,.24) 0%, rgba(255,255,255,.12) 18%, transparent 38%), linear-gradient(125deg, rgba(255,255,255,.18) 0%, transparent 24%, rgba(255,255,255,.16) 48%, transparent 72%), linear-gradient(135deg, #f5d3de 0%, #c7e2f8 34%, #80cffa 66%, #bceef1 100%)',

    neonPink:
        'radial-gradient(circle at 50% 78%, rgba(74,163,255,.55), transparent 42%), radial-gradient(circle at 65% 20%, rgba(104,231,223,.35), transparent 35%), radial-gradient(circle at 20% 50%, rgba(186,214,255,.28), transparent 45%), linear-gradient(135deg, #eef0f8 0%, #d7e7f6 25%, #9dd4ff 55%, #d3e7f7 78%, #f2f3f8 100%)',

    vaporwave:
        'radial-gradient(circle at 20% 90%, rgba(255,0,255,.35), transparent 35%), radial-gradient(circle at 75% 10%, rgba(0,200,255,.4), transparent 45%), linear-gradient(145deg, rgba(255,255,255,.04) 20%, transparent 20%, transparent 45%, rgba(255,255,255,.03) 45%, rgba(255,255,255,.03) 65%, transparent 65%), linear-gradient(180deg, #09132d 0%, #040a18 100%)',

    cyberBlue:
        'radial-gradient(circle at 85% 15%, rgba(0,180,255,.45), transparent 40%), radial-gradient(circle at 15% 10%, rgba(255,0,128,.25), transparent 35%), linear-gradient(150deg, transparent 25%, rgba(255,255,255,.04) 25%, rgba(255,255,255,.04) 40%, transparent 40%), linear-gradient(150deg, transparent 45%, rgba(255,255,255,.03) 45%, rgba(255,255,255,.03) 60%, transparent 60%), linear-gradient(180deg, #071a42 0%, #020b25 45%, #000814 100%)',

    midnight:
        'radial-gradient(circle at 85% 20%, rgba(66,54,120,.55), transparent 35%), radial-gradient(circle at 15% 85%, rgba(20,58,92,.45), transparent 40%), linear-gradient(135deg, #050714 0%, #11142b 35%, #1b1635 65%, #050714 100%)'
} as const

export type StoryBackgroundKey = keyof typeof STORY_BACKGROUNDS

export const BACKGROUND_KEYS = Object.keys(
    STORY_BACKGROUNDS
) as StoryBackgroundKey[]

export const DEFAULT_STORY_BACKGROUND: StoryBackgroundKey = 'modernGradient'

export const getStoryBackground = (
    background?: string | null
): string => {
    if (!background) return STORY_BACKGROUNDS[DEFAULT_STORY_BACKGROUND]

    return (
        STORY_BACKGROUNDS[background as StoryBackgroundKey] ??
        STORY_BACKGROUNDS[DEFAULT_STORY_BACKGROUND]
    )
}

interface BackgroundPickerProps {
    background: string
    setBackground: (background: StoryBackgroundKey) => void
    isDisabled?: boolean
}

const BackgroundPicker: VFC<BackgroundPickerProps> = ({
    background,
    setBackground,
    isDisabled
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const { t } = useTranslation()

    const buttonRef = useRef<HTMLButtonElement | null>(null)
    const pickerRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (!isOpen) return

        const handleDocumentMouseDown = (event: MouseEvent) => {
            const target = event.target as Node

            const clickedButton = buttonRef.current?.contains(target)
            const clickedPicker = pickerRef.current?.contains(target)

            if (clickedButton || clickedPicker) return

            setIsOpen(false)
        }

        document.addEventListener('mousedown', handleDocumentMouseDown)

        return () => {
            document.removeEventListener('mousedown', handleDocumentMouseDown)
        }
    }, [isOpen])

    return (
        <>
            <SidebarItem
                buttonRef={buttonRef}
                onClick={e => {
                    e.preventDefault()
                    e.stopPropagation()

                    if (isDisabled) return

                    setIsOpen(prev => !prev)
                }}
                text={t('background')}
                icon={
                    <div
                        className={styles.icon}
                        style={{ background: getStoryBackground(background) }}
                    />
                }
                isDisabled={isDisabled}
            />

            {isOpen &&
                createPortal(
                    <div
                        ref={pickerRef}
                        className={styles.picker}
                        onMouseDown={e => {
                            e.preventDefault()
                            e.stopPropagation()
                        }}
                        onClick={e => {
                            e.preventDefault()
                            e.stopPropagation()
                        }}
                    >
                        {BACKGROUND_KEYS.map(key => (
                            <div
                                onClick={e => {
                                    e.preventDefault()
                                    e.stopPropagation()

                                    setBackground(key)
                                    setIsOpen(false)
                                }}
                                key={key}
                                className={styles.pickerItem}
                                style={{
                                    background: STORY_BACKGROUNDS[key]
                                }}
                            />
                        ))}
                    </div>,
                    document.body
                )}
        </>
    )
}

export default BackgroundPicker